import { ProductType, SourceType } from '@core/api';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { createSelector } from 'reselect';
import { LicenseConstants } from '../constants/LicenseConstants.js';
import { LicenseHelper } from '../helpers/LicenseHelper.js';
import { LicenseStore } from '../store/LicenseStore.js';
dayjs.extend(isSameOrBefore);
export var permittedUndefinedValues = [
    'listFieldValuesQuotaPerOrgLimit'
];
export var editableSearchOrder = [
    'maxPipelinesLimit',
    'maxPipelineConditionsLimit',
    'maxPipelineStatementsLimit',
    'queryQuotaPerOrgLimit',
    'querySuggestQuotaPerOrgLimit',
    'listFieldValuesQuotaPerOrgLimit'
];
export var editableContentOrder = [
    'numberOfSourcesLimit',
    'numberOfMappingPerSourcesLimit',
    'numberOfSourceTypesLimit',
    'numberOfFieldsLimit',
    'licenseExtensionsLimit',
    'numberOfExtensionsPerSourceLimit',
    'numberOfSecurityProvidersLimit',
    'PushApiFileSizeLimit',
    'numberOfDocumentsLimit',
    'pushApiMaximumDailyInvocations',
    'pushApiMaximumDailyDocumentLimit',
    'pushApiMaximumDailyRelationshipLimit'
];
export var contentHistoryLimits = [
    'numberOfDocumentsLimit',
    'pushApiMaximumDailyInvocations',
    'pushApiMaximumDailyDocumentLimit',
    'pushApiMaximumDailyRelationshipLimit'
];
export var throttlingLimits = [
    'listFieldValuesQuotaPerOrgLimit',
    'numberOfDailyEmailNotificationsSentPerOrganizationLimit',
    'pushApiMaximumDailyDocumentLimit',
    'pushApiMaximumDailyInvocations',
    'pushApiMaximumDailyRelationshipLimit',
    'queryQuotaPerOrgLimit',
    'querySuggestQuotaPerOrgLimit',
    'passageRetrievalQuotaPerOrgLimit',
    'relevanceGenerativeAnsweringQueryPerSecondLimit',
    'streamApiMaximumBurstInvocation',
    'pushApiBurstRelationshipLimit',
    'pushApiBurstDocumentsLimit',
    'burstPartialUpdatesLimit'
];
var unaddableSources = [
    SourceType.CUSTOM
];
var getLicense = function getLicense(state) {
    return state.license;
};
var getLicenseProperties = createSelector(getLicense, function(license) {
    return license === null || license === void 0 ? void 0 : license.properties;
});
var getConnectors = createSelector(getLicense, function(license) {
    var _ref;
    return (_ref = license === null || license === void 0 ? void 0 : license.connectors) !== null && _ref !== void 0 ? _ref : [];
});
var getAvailableConnectors = function getAvailableConnectors() {
    return createSelector(getLicense, function(license) {
        var _ref;
        var _license_connectors;
        return (_ref = license === null || license === void 0 ? void 0 : (_license_connectors = license.connectors) === null || _license_connectors === void 0 ? void 0 : _license_connectors.filter(function(connector) {
            return !unaddableSources.includes(connector.type) && Object.values(SourceType).includes(connector.type);
        })) !== null && _ref !== void 0 ? _ref : [];
    });
};
var getSearchProperties = createSelector(getLicenseProperties, function(licenseProperties) {
    return licenseProperties === null || licenseProperties === void 0 ? void 0 : licenseProperties.searchapi;
});
var getContentProperties = createSelector(getLicenseProperties, function(licenseProperties) {
    return licenseProperties === null || licenseProperties === void 0 ? void 0 : licenseProperties.content;
});
var getOrganizationProperties = createSelector(getLicenseProperties, function(licenseProperties) {
    return licenseProperties === null || licenseProperties === void 0 ? void 0 : licenseProperties.organization;
});
var getUsageAnalyticsProperties = createSelector(getLicenseProperties, function(licenseProperties) {
    return licenseProperties === null || licenseProperties === void 0 ? void 0 : licenseProperties.usageAnalytics;
});
var getMachineLearningProperties = createSelector(getLicenseProperties, function(licenseProperties) {
    return licenseProperties === null || licenseProperties === void 0 ? void 0 : licenseProperties.machineLearning;
});
var getCommerceProperties = createSelector(getLicenseProperties, function(licenseProperties) {
    return licenseProperties === null || licenseProperties === void 0 ? void 0 : licenseProperties.commerce;
});
var getInternalProperties = createSelector(getLicenseProperties, function(licenseProperties) {
    return licenseProperties === null || licenseProperties === void 0 ? void 0 : licenseProperties.internal;
});
var getInProductExperienceProperties = createSelector(getLicenseProperties, function(licenseProperties) {
    return licenseProperties === null || licenseProperties === void 0 ? void 0 : licenseProperties.inProductExperience;
});
var getLicenseType = createSelector(getLicense, function(license) {
    return license === null || license === void 0 ? void 0 : license.type;
});
var getProductType = createSelector(getLicense, function(license) {
    return license === null || license === void 0 ? void 0 : license.productType;
});
var getProductName = createSelector(getLicense, function(license) {
    return license === null || license === void 0 ? void 0 : license.productName;
});
var getIndexType = createSelector(getLicense, function(license) {
    return license === null || license === void 0 ? void 0 : license.indexType;
});
var getExpirationDate = createSelector(getLicense, function(license) {
    return license === null || license === void 0 ? void 0 : license.expirationDate;
});
var getProductEdition = createSelector(getLicense, function(license) {
    return license === null || license === void 0 ? void 0 : license.productEdition;
});
var getEntitlements = createSelector(getLicense, function(license) {
    return license === null || license === void 0 ? void 0 : license.entitlements;
});
var getModelConfigurability = createSelector(getLicenseProperties, function(licenseProperties) {
    var _licenseProperties_machineLearning;
    return licenseProperties === null || licenseProperties === void 0 ? void 0 : (_licenseProperties_machineLearning = licenseProperties.machineLearning) === null || _licenseProperties_machineLearning === void 0 ? void 0 : _licenseProperties_machineLearning.modelConfigurability;
});
var isABTestEnabled = createSelector(getSearchProperties, function(searchApiProperties) {
    var _ref;
    return (_ref = searchApiProperties === null || searchApiProperties === void 0 ? void 0 : searchApiProperties.abTestEnabled) !== null && _ref !== void 0 ? _ref : true;
});
var isResourceSnapshotsEnabled = createSelector(getOrganizationProperties, function(organizationProperties) {
    var _ref;
    return (_ref = organizationProperties === null || organizationProperties === void 0 ? void 0 : organizationProperties.snapshotsEnabled) !== null && _ref !== void 0 ? _ref : false;
});
var isModelTestingAvailable = createSelector(getMachineLearningProperties, function(machineLearningProperties) {
    var _ref;
    return (_ref = machineLearningProperties === null || machineLearningProperties === void 0 ? void 0 : machineLearningProperties.modelTesterEnabled) !== null && _ref !== void 0 ? _ref : true;
});
var isIntelligentTermDetectionEnabled = function isIntelligentTermDetectionEnabled(state) {
    var _LicenseSelectors_getMachineLearningProperties;
    return !!((_LicenseSelectors_getMachineLearningProperties = LicenseSelectors.getMachineLearningProperties(state)) === null || _LicenseSelectors_getMachineLearningProperties === void 0 ? void 0 : _LicenseSelectors_getMachineLearningProperties[LicenseConstants.ITDEnable]);
};
var isIpxEnabled = function isIpxEnabled(state) {
    var _LicenseSelectors_getInProductExperienceProperties;
    return !!((_LicenseSelectors_getInProductExperienceProperties = LicenseSelectors.getInProductExperienceProperties(state)) === null || _LicenseSelectors_getInProductExperienceProperties === void 0 ? void 0 : _LicenseSelectors_getInProductExperienceProperties[LicenseConstants.isIpxEnabled]);
};
var isSitecoreLicense = createSelector(getLicenseType, function(licenseType) {
    return licenseType ? LicenseConstants.SITECORE_LICENSES.includes(licenseType) : false;
});
var isTrialLicense = createSelector(getLicense, function(license) {
    return license ? LicenseHelper.isTrial(license) : false;
});
var isProductionOrganization = createSelector(getProductType, function(productType) {
    return productType === ProductType.STANDARD;
});
var isSearchGroupsEnabled = createSelector(getSearchProperties, function(properties) {
    var _ref;
    return (_ref = properties === null || properties === void 0 ? void 0 : properties.groupsEnabled) !== null && _ref !== void 0 ? _ref : true;
});
var isTrialExpired = createSelector(getLicense, function(license) {
    return license ? LicenseHelper.isTrialExpired(license) : false;
});
var hasExpired = function hasExpired() {
    var state = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : LicenseStore.getState();
    return isTrialExpired(state);
};
var isTrialOrg = function isTrialOrg() {
    var state = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : LicenseStore.getState();
    return isTrialLicense(state);
};
export var LicenseSelectors = {
    isTrialExpired: isTrialExpired,
    hasExpired: hasExpired,
    getLicense: getLicense,
    getConnectors: getConnectors,
    getAvailableConnectors: getAvailableConnectors,
    getSearchProperties: getSearchProperties,
    getContentProperties: getContentProperties,
    getCommerceProperties: getCommerceProperties,
    getInternalProperties: getInternalProperties,
    getInProductExperienceProperties: getInProductExperienceProperties,
    getOrganizationProperties: getOrganizationProperties,
    getUsageAnalyticsProperties: getUsageAnalyticsProperties,
    getMachineLearningProperties: getMachineLearningProperties,
    getLicenseType: getLicenseType,
    getProductType: getProductType,
    getProductName: getProductName,
    getIndexType: getIndexType,
    getExpirationDate: getExpirationDate,
    getProductEdition: getProductEdition,
    getEntitlements: getEntitlements,
    getModelConfigurability: getModelConfigurability,
    isABTestEnabled: isABTestEnabled,
    isProductionOrganization: isProductionOrganization,
    isResourceSnapshotsEnabled: isResourceSnapshotsEnabled,
    isSitecoreLicense: isSitecoreLicense,
    isTrialOrg: isTrialOrg,
    isTrialLicense: isTrialLicense,
    isModelTestingAvailable: isModelTestingAvailable,
    isIntelligentTermDetectionEnabled: isIntelligentTermDetectionEnabled,
    isIpxEnabled: isIpxEnabled,
    isSearchGroupsEnabled: isSearchGroupsEnabled
};

//# sourceMappingURL=LicenseSelectors.js.map